import { MutationTree } from 'vuex';
import { ErrorDetail } from '../../types';
import { RandomizableAccount } from '../account/types';
import { NodeAccountState, NodeAccountUniqueId, NodeAndAccount } from './types';
import * as types from './mutation-types';

// パスワードランダム化のエラーをクリアする
const resetChangesRandomizePasswordErrors = (state: NodeAccountState): void => {
    const randomizableAccountState = state.randomizableAccount;
    randomizableAccountState.error = false;
    randomizableAccountState.errorCode = undefined;
    randomizableAccountState.errorMsg = undefined;

    const manuallyRandomizePasswordOrSshKeyState = state.manuallyRandomizePasswordOrSshKey;
    manuallyRandomizePasswordOrSshKeyState.error = false;
    manuallyRandomizePasswordOrSshKeyState.errorCode = undefined;
    manuallyRandomizePasswordOrSshKeyState.errorMsg = undefined;
};

export const mutations: MutationTree<NodeAccountState> = {
    // ノードグループ・アカウント検索系
    [types.INDEX_START](state) {
        const searchState = state.index;
        searchState.error = false;
        searchState.errorCode = undefined;
        searchState.errorMsg = undefined;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.INDEX_SUCCESS](
        state,
        payload: { count: number; accounts: Array<NodeAndAccount>; node_group_ids: Array<string> }
    ) {
        state.nodeAndAccounts = payload.accounts;
        state.nodeAndAccountCount = payload.count;
        state.nodeGroupIds = payload.node_group_ids;

        const searchState = state.index;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.INDEX_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const searchState = state.index;
        searchState.errorCode = payload.errorDetails[0].code;
        searchState.errorMsg = payload.errorDetails[0].message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // パスワードランダム化対象のノード管理者アカウントと作業者アカウントの取得
    [types.NODE_RANDOMIZABLE_ACCOUNT_START](state) {
        resetChangesRandomizePasswordErrors(state);
        const randomizableAccountState = state.randomizableAccount;
        randomizableAccountState.isProcessing = true;
        randomizableAccountState.isFetchSuccess = false;
    },

    [types.NODE_RANDOMIZABLE_ACCOUNT_SUCCESS](
        state,
        payload: { count: number; account_type: string; accounts: Array<RandomizableAccount> }
    ) {
        state.nodeRandomizableAccounts = payload.accounts;
        state.nodeRandomizableAccountCount = payload.count;
        state.nodeRandomizableAccountType = payload.account_type;

        const randomizableAccountState = state.randomizableAccount;
        randomizableAccountState.isProcessing = false;
        randomizableAccountState.isFetchSuccess = true;
    },

    [types.NODE_RANDOMIZABLE_ACCOUNT_FAILURE](state, payload: { code: string; message: string }) {
        const randomizableAccountState = state.randomizableAccount;
        randomizableAccountState.errorCode = payload.code;
        randomizableAccountState.errorMsg = payload.message;
        randomizableAccountState.error = true;
        randomizableAccountState.isProcessing = false;
        randomizableAccountState.isFetchSuccess = false;
    },

    // ノードアカウントのパスワード・SSH鍵の手動ランダム化
    [types.MANUALLY_RANDOMIZE_PASSWORD_OR_SSH_KEY_START](state) {
        resetChangesRandomizePasswordErrors(state);
        const manuallyRandomizePasswordOrSshKeyState = state.manuallyRandomizePasswordOrSshKey;
        manuallyRandomizePasswordOrSshKeyState.isProcessing = true;
        manuallyRandomizePasswordOrSshKeyState.isFetchSuccess = false;
    },

    [types.MANUALLY_RANDOMIZE_PASSWORD_OR_SSH_KEY_SUCCESS](state) {
        const manuallyRandomizePasswordOrSshKeyState = state.manuallyRandomizePasswordOrSshKey;
        manuallyRandomizePasswordOrSshKeyState.isProcessing = false;
        manuallyRandomizePasswordOrSshKeyState.isFetchSuccess = true;
    },

    [types.MANUALLY_RANDOMIZE_PASSWORD_OR_SSH_KEY_FAILURE](state, payload: { code: string; message: string }) {
        const manuallyRandomizePasswordOrSshKeyState = state.manuallyRandomizePasswordOrSshKey;
        manuallyRandomizePasswordOrSshKeyState.errorCode = payload.code;
        manuallyRandomizePasswordOrSshKeyState.errorMsg = payload.message;
        manuallyRandomizePasswordOrSshKeyState.error = true;
        manuallyRandomizePasswordOrSshKeyState.isProcessing = false;
        manuallyRandomizePasswordOrSshKeyState.isFetchSuccess = false;
    },

    // ノードアカウント除外リスト取得
    [types.RESERVABLE_FOR_WORK_START](state) {
        const reservableForWorkState = state.reservableForWork;
        reservableForWorkState.isProcessing = true;
        reservableForWorkState.isFetchSuccess = false;
    },

    [types.RESERVABLE_FOR_WORK_SUCCESS](state, payload: Array<NodeAccountUniqueId>) {
        state.nodeAccountReservableForWork = payload;

        const reservableForWorkState = state.reservableForWork;
        reservableForWorkState.isProcessing = false;
        reservableForWorkState.isFetchSuccess = true;
    },

    [types.RESERVABLE_FOR_WORK_FAILURE](state, payload: { code: string; message: string }) {
        const reservableForWorkState = state.reservableForWork;
        reservableForWorkState.errorCode = payload.code;
        reservableForWorkState.errorMsg = payload.message;
        reservableForWorkState.error = true;
        reservableForWorkState.isProcessing = false;
        reservableForWorkState.isFetchSuccess = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const indexState = state.index;
        indexState.isProcessing = false;
        indexState.isFetchSuccess = false;

        const randomizableAccountState = state.randomizableAccount;
        randomizableAccountState.isProcessing = false;
        randomizableAccountState.isFetchSuccess = false;

        const manuallyRandomizePasswordOrSshKeyState = state.manuallyRandomizePasswordOrSshKey;
        manuallyRandomizePasswordOrSshKeyState.isProcessing = false;
        manuallyRandomizePasswordOrSshKeyState.isFetchSuccess = false;

        const reservableForWorkState = state.reservableForWork;
        reservableForWorkState.isProcessing = false;
        reservableForWorkState.isFetchSuccess = false;
    },

    // パスワードランダム化に関するエラーのみリセットする
    [types.RESET_RANDOMIZE_PASSWORD_ERROR](state) {
        resetChangesRandomizePasswordErrors(state);
    },

    [types.CLEAR_ALL](state) {
        state.nodeAndAccounts = undefined;
        state.nodeAndAccountCount = 0;
        state.nodeGroupIds = undefined;
        state.nodeRandomizableAccounts = undefined;
        state.nodeRandomizableAccountCount = 0;
        state.nodeRandomizableAccountType = undefined;
        state.nodeAccountReservableForWork = undefined;
        state.index.isProcessing = false;
        state.index.isFetchSuccess = false;
        state.index.error = false;
        state.index.errorCode = undefined;
        state.index.errorMsg = undefined;
        state.randomizableAccount.isProcessing = false;
        state.randomizableAccount.isFetchSuccess = false;
        state.randomizableAccount.error = false;
        state.randomizableAccount.errorCode = undefined;
        state.randomizableAccount.errorMsg = undefined;
        state.manuallyRandomizePasswordOrSshKey.isProcessing = false;
        state.manuallyRandomizePasswordOrSshKey.isFetchSuccess = false;
        state.manuallyRandomizePasswordOrSshKey.error = false;
        state.manuallyRandomizePasswordOrSshKey.errorCode = undefined;
        state.manuallyRandomizePasswordOrSshKey.errorMsg = undefined;
        state.reservableForWork.isProcessing = false;
        state.reservableForWork.isFetchSuccess = false;
        state.reservableForWork.error = false;
        state.reservableForWork.errorCode = undefined;
        state.reservableForWork.errorMsg = undefined;
    },
};
